import type { MouseEvent } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import type { TChangeEventHandler, TFocusEventHandler } from '@alfa-bank/site-input/models';

import type {
    IActionsClass,
    IActionsBase,
    TNames,
    ICheckboxElementProps,
    TRadioTags,
    TOnFioSelect,
} from '../models';
import { getSuggestions } from '../helpers/suggestions-fetch';

export class FormActionsBase implements IActionsBase {
    /** Данные для аналитики */
    protected analytics: IActionsClass['analytics'];

    /** Колбеки формы */
    protected cb: IActionsClass['cb'];

    /** Настройки полей формы */
    protected fields: IActionsClass['fields'];

    /** Элементы формы, последующие вводу */
    protected middleNameRef: IActionsClass['middleNameRef'];

    /** Хелпер изменения состояния формы */
    protected setState: IActionsClass['setState'];

    /** Состояние формы */
    protected state: IActionsClass['state'];

    constructor({ analytics, cb, fields, middleNameRef, setState, state }: IActionsClass) {
        this.analytics = analytics;
        this.cb = cb;
        this.fields = fields;
        this.setState = setState;
        this.state = state;
        this.middleNameRef = middleNameRef;
        // Для правильной работы наследования нужны функции и биндинги
        this.onBlurHandler = this.onBlurHandler.bind(this);
        this.onChangeHandler = this.onChangeHandler.bind(this);
        this.onFocusHandler = this.onFocusHandler.bind(this);
        this.onSelectHandler = this.onSelectHandler.bind(this);
        this.onCheckbox = this.onCheckbox.bind(this);
        this.makeProps = this.makeProps.bind(this);
        this.onClick = this.onClick.bind(this);
    }

    /** Действия при выходе из элемента формы */
    onBlurHandler(field: TNames): TFocusEventHandler {
        return event => {
            const { value = '' } = event;
            const blurEvent = {
                ...event,
                value: String(value),
                field: this.fields[field],
            };

            this.cb.onBlur(blurEvent);
        };
    }

    /** Действия при изменении элемента формы */
    onChangeHandler(field: TNames): TChangeEventHandler {
        return event => {
            const { value = '' } = event;

            this.cb.onChange({
                ...event,
                value: String(value),
                field: this.fields[field],
            });
        };
    }

    /** Действия при наведении на элемент формы */
    onFocusHandler(field: TNames): TFocusEventHandler {
        return event => {
            const { value = '' } = event;

            this.cb.onFocus({
                ...event,
                value: String(value),
                field: this.fields[field],
            });
        };
    }

    /** Действия при выборе значения */
    onSelectHandler(field: TNames): TOnFioSelect {
        return event => {
            const { value } = event;

            // Загрузить подсказки в соответствии с новыми
            this.loadSuggestions(field, value);
            // Отправить колбек о селекте
            this.cb.onSelect({ ...event, field: this.fields[field] });
        };
    }

    onCancelHandler = (field: TNames) => () => this.cb.onCancel(this.fields[field]);

    /** Действия при вызове от Checkbox */
    onCheckbox({ checked }: ICheckboxElementProps) {
        const { analytics, fields, setState } = this;
        const name = checked ? '-' : '';

        // Изменить и валидировать значение отчества
        fields.middleName.input.onChange(name);
        fields.middleName.input.onBlur();

        setState('hasMiddleName', !checked);
        // Обнулить отложенный middleNameBlurEvent
        setState('middleNameBlurEvent', undefined);

        if (analytics?.hasMiddleName) sendEvent(analytics?.hasMiddleName);
    }

    onClick(field: TNames) {
        return (event: MouseEvent<HTMLInputElement>) => {
            const { analytics = {} } = this;

            if (Object.keys(analytics).length) sendEvent(analytics[field]);
            this.cb.onClick(event);
        };
    }

    onMouseEnterCheckbox = () => this.setState('mouseOverCheckbox', true);

    onMouseLeaveCheckbox = () => {
        const {
            setState,
            state: { middleNameBlurEvent },
        } = this;

        // Если был отложен blur - вызвать его сейчас
        if (middleNameBlurEvent) {
            this.cb.onBlur(middleNameBlurEvent);
            setState('middleNameBlurEvent', undefined);
        }
        setState('mouseOverCheckbox', false);
    };

    onRadioTags: TRadioTags = () => {};

    /** Создает общие пропсы полей */
    public makeProps(name: TNames) {
        const { data = {} } = this.fields[name].meta;

        return {
            caption: data.caption,
            field: this.fields[name],
            onBlur: this.onBlurHandler(name),
            onCancel: this.onCancelHandler(name),
            onChange: this.onChangeHandler(name),
            onClick: this.onClick(name),
            onFocus: this.onFocusHandler(name),
            onSelect: this.onSelectHandler(name),
            options: this.state.options[name],
            placeholder: data.placeholder,
            dataTestId: data.dataTestId,
        };
    }

    /** Загрузить подсказки и добавить их в состояние поля */
    public loadSuggestions = (field: TNames, value: string) =>
        getSuggestions(field, value, this.state.supposeGender)
            .then(options => this.setState('options', field, options))
            .catch(e => console.error(e));
}
